import { useState } from 'react'
import { Link, NavLink } from 'react-router-dom'
import { AnimatePresence, motion } from 'framer-motion'
import {
  ChevronDown,
  ChevronRight,
  Heart,
  Home,
  Info,
  LayoutDashboard,
  Mail,
  PackageSearch,
  Phone,
  ShoppingBag,
  Store,
  X,
} from 'lucide-react'
import { Logo } from '@/components/ui/Logo'
import { WhatsAppIcon } from '@/components/ui/WhatsAppIcon'
import { useSettings } from '@/context/SettingsContext'
import { useAuth } from '@/context/AuthContext'
import { useLockBodyScroll } from '@/hooks/useLockBodyScroll'
import { ROUTES } from '@/constants'
import { cn } from '@/utils/cn'
import { whatsAppUrl } from '@/utils/whatsapp'

const links = [
  { to: ROUTES.home, label: 'Home', Icon: Home, end: true },
  { to: ROUTES.shop, label: 'Shop All', Icon: Store, end: true },
  { to: ROUTES.wishlist, label: 'Wishlist', Icon: Heart },
  { to: ROUTES.cart, label: 'Cart', Icon: ShoppingBag },
  { to: ROUTES.trackOrder, label: 'Track Order', Icon: PackageSearch },
  { to: ROUTES.about, label: 'About', Icon: Info },
  { to: ROUTES.contact, label: 'Contact', Icon: Mail },
]

/**
 * Phone navigation drawer. Carries everything the desktop header spreads
 * across three rows — the category tree (with sub-categories, which the
 * desktop strip shows in hover dropdowns), the site links, and a quick way
 * to call or WhatsApp the shop.
 *
 * @param {{ open: boolean, onClose: () => void, tree?: any[] }} props
 */
export function MobileMenu({ open, onClose, tree = [] }) {
  const { brandName, supportPhone, whatsappNumber } = useSettings()
  const { isStaff } = useAuth()
  useLockBodyScroll(open)

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            className="fixed inset-0 z-50 bg-black/40"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
          />
          <motion.aside
            initial={{ x: '-100%' }}
            animate={{ x: 0 }}
            exit={{ x: '-100%' }}
            transition={{ type: 'tween', duration: 0.25 }}
            className="fixed inset-y-0 left-0 z-50 flex w-[85%] max-w-sm flex-col bg-white shadow-modal"
            aria-label="Mobile navigation"
          >
            <div className="flex items-center justify-between border-b border-charcoal-100 px-4 py-3">
              <Link to={ROUTES.home} onClick={onClose} className="flex items-center gap-2 font-bold">
                <Logo size={40} />
                <span className="text-base">{brandName}</span>
              </Link>
              <button
                type="button"
                aria-label="Close menu"
                onClick={onClose}
                className="grid h-11 w-11 place-items-center rounded-full hover:bg-sand-200"
              >
                <X size={22} />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto overscroll-contain">
              <nav className="px-2 py-2">
                {links.map(({ to, label, Icon, end }) => (
                  <NavLink
                    key={to}
                    to={to}
                    end={end}
                    onClick={onClose}
                    className={({ isActive }) =>
                      cn(
                        'flex min-h-12 items-center gap-3 rounded-xl px-3 text-[15px] font-medium transition',
                        isActive ? 'bg-coral-50 text-coral' : 'text-charcoal hover:bg-sand-100'
                      )
                    }
                  >
                    <Icon size={20} className="flex-shrink-0" />
                    {label}
                  </NavLink>
                ))}
                {isStaff && (
                  <NavLink
                    to={ROUTES.adminDashboard}
                    onClick={onClose}
                    className="flex min-h-12 items-center gap-3 rounded-xl px-3 text-[15px] font-medium text-charcoal hover:bg-sand-100"
                  >
                    <LayoutDashboard size={20} className="flex-shrink-0" />
                    Admin
                  </NavLink>
                )}
              </nav>

              {tree.length > 0 && (
                <div className="border-t border-charcoal-100 px-2 py-2">
                  <p className="px-3 pb-1 pt-2 text-xs font-bold uppercase tracking-wide text-charcoal-400">
                    Categories
                  </p>
                  {tree.map((c) => (
                    <CategoryRow key={c.id} category={c} onNavigate={onClose} />
                  ))}
                </div>
              )}

              {(supportPhone || whatsappNumber) && (
                <div className="border-t border-charcoal-100 px-2 py-3">
                  <p className="px-3 pb-2 pt-1 text-xs font-bold uppercase tracking-wide text-charcoal-400">
                    Need help?
                  </p>
                  <div className="grid grid-cols-2 gap-2 px-2">
                    {supportPhone && (
                      <a
                        href={`tel:+${supportPhone}`}
                        className="flex min-h-11 items-center justify-center gap-2 rounded-full border border-charcoal-200 text-sm font-semibold text-charcoal"
                      >
                        <Phone size={16} className="text-coral" /> Call us
                      </a>
                    )}
                    {whatsappNumber && (
                      <a
                        href={whatsAppUrl('', whatsappNumber)}
                        target="_blank"
                        rel="noreferrer"
                        className="flex min-h-11 items-center justify-center gap-2 rounded-full bg-[#25D366]/10 text-sm font-semibold text-charcoal"
                      >
                        <span className="text-[#25D366]"><WhatsAppIcon size={18} /></span> WhatsApp
                      </a>
                    )}
                  </div>
                </div>
              )}
              <div className="pb-safe h-4" />
            </div>
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  )
}

/**
 * One top-level category. With sub-categories the chevron expands them in
 * place; the name itself always goes straight to the category's shop page.
 */
function CategoryRow({ category, onNavigate }) {
  const [expanded, setExpanded] = useState(false)
  const to = `${ROUTES.shop}?category=${encodeURIComponent(category.id)}`
  const hasChildren = category.children.length > 0

  return (
    <div>
      <div className="flex items-center">
        <Link
          to={to}
          onClick={onNavigate}
          className="flex min-h-12 flex-1 items-center rounded-xl px-3 text-[15px] font-medium text-charcoal hover:bg-sand-100"
        >
          {category.name}
        </Link>
        {hasChildren ? (
          <button
            type="button"
            aria-label={expanded ? `Collapse ${category.name}` : `Expand ${category.name}`}
            aria-expanded={expanded}
            onClick={() => setExpanded((v) => !v)}
            className="grid h-12 w-12 flex-shrink-0 place-items-center rounded-xl text-charcoal-600 hover:bg-sand-100"
          >
            <ChevronDown size={18} className={cn('transition-transform', expanded && 'rotate-180')} />
          </button>
        ) : (
          <ChevronRight size={16} className="mr-4 text-charcoal-300" />
        )}
      </div>
      {hasChildren && expanded && (
        <div className="mb-1 ml-4 border-l border-charcoal-100 pl-2">
          <Link
            to={to}
            onClick={onNavigate}
            className="flex min-h-11 items-center rounded-lg px-3 text-sm font-semibold text-charcoal hover:bg-sand-100"
          >
            All {category.name}
          </Link>
          {category.children.map((sub) => (
            <Link
              key={sub.id}
              to={`${ROUTES.shop}?category=${encodeURIComponent(sub.id)}`}
              onClick={onNavigate}
              className="flex min-h-11 items-center rounded-lg px-3 text-sm text-charcoal-700 hover:bg-sand-100"
            >
              {sub.name}
            </Link>
          ))}
        </div>
      )}
    </div>
  )
}
